/**
 * Date display utilities — discovr FE
 *
 * Convention:
 *  - All dates are stored and passed to APIs in UTC (ISO 8601).
 *  - All display to users uses IST (Asia/Kolkata, UTC+5:30).
 *  - Future: swap DISPLAY_TZ for a user-preference lookup when
 *    multi-geography support lands.
 *
 * Never call .toLocaleDateString() / .toLocaleString() without an
 * explicit timeZone option on API-sourced dates.
 */

export const DISPLAY_TZ = 'Asia/Kolkata'; // IST — UTC+5:30
/** IST has no daylight saving, so a fixed offset is exact. */
export const DISPLAY_TZ_OFFSET_MS = 330 * 60_000;

/**
 * Format an ISO timestamp as a short date in IST.
 * e.g. "7 May 2026"
 */
export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', {
      timeZone: DISPLAY_TZ,
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

/**
 * Format a valid ISO timestamp as day and month in IST, for tight captions.
 * e.g. "7 May"
 */
export function fmtDayMonth(iso: string): string {
  return new Date(iso).toLocaleDateString('en-IN', { timeZone: DISPLAY_TZ, day: 'numeric', month: 'short' });
}

/**
 * Format an ISO timestamp as date + time in IST.
 * e.g. "7 May 2026, 08:00 PM"
 */
export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      timeZone: DISPLAY_TZ,
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

/**
 * Format an ISO timestamp as time only in IST.
 * e.g. "08:00 PM"
 */
export function fmtTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString('en-IN', {
      timeZone: DISPLAY_TZ,
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

/**
 * UTC calendar date string for API calls — never use local timezone.
 * e.g. utcDateStr(0) → "2026-05-07"  (today UTC)
 *      utcDateStr(6) → "2026-05-01"  (6 days ago UTC)
 */
export function utcDateStr(daysAgo = 0): string {
  return new Date(Date.now() - daysAgo * 86_400_000).toISOString().split('T')[0] as string;
}
